import { type FormEvent, type SyntheticEvent, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useCreateWorker } from "../../hooks/useCreateWorker";
import { TextField } from "../../components/TextField";
import { Button } from "../../components/Button";
import { Banner } from "../../components/Banner";
import { PinShownOnceBanner } from "./PinShownOnceBanner";

interface NewWorkerModalProps {
  onClose: () => void;
}

interface RevealState {
  pin: string;
}

export function NewWorkerModal({ onClose }: NewWorkerModalProps) {
  const intl = useIntl();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [pinFormatError, setPinFormatError] = useState("");
  const [revealed, setRevealed] = useState<RevealState | null>(null);

  const createWorker = useCreateWorker();

  useEffect(() => {
    dialogRef.current?.showModal();
    return () => {
      dialogRef.current?.close();
    };
  }, []);

  const handleClose = () => {
    setRevealed(null);
    onClose();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setPinFormatError("");

    if (!/^\d{4}$/.test(pin)) {
      setPinFormatError(intl.formatMessage({ id: "ERR_VALIDATION_PIN_FORMAT" }));
      return;
    }

    const submittedPin = pin;
    createWorker.mutate(
      { name, pin: submittedPin },
      {
        onSuccess: () => {
          setRevealed({ pin: submittedPin });
        },
      },
    );
  };

  const handleDialogCancel = (e: SyntheticEvent) => {
    e.preventDefault(); // prevent native close; use Done button only
  };

  const isServerError409 =
    createWorker.isError && createWorker.error?.code === "ERR_PIN_ALREADY_USED";

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleDialogCancel}
      data-testid="new-worker-modal"
      className="rounded-lg border border-divider bg-surface-raised shadow-lg p-0 backdrop:bg-black/40 inline-size-[480px] max-inline-size-[calc(100vw-2rem)]"
    >
      {revealed ? (
        <div className="pbs-8 pbe-8">
          <PinShownOnceBanner pin={revealed.pin} onClose={handleClose} />
        </div>
      ) : (
        <div className="ps-6 pe-6 pbs-6 pbe-6">
          <h2 className="text-xl font-semibold text-on-surface mbe-6">
            <FormattedMessage id="LABEL_NEW_WORKER" />
          </h2>

          {pinFormatError && (
            <div className="mbe-4">
              <Banner variant="error" data-testid="pin-format-error-banner">
                {pinFormatError}
              </Banner>
            </div>
          )}

          {isServerError409 && (
            <div className="mbe-4">
              <Banner variant="error" data-testid="pin-already-used-banner">
                <FormattedMessage id="ERR_PIN_ALREADY_USED" />
              </Banner>
            </div>
          )}

          {createWorker.isError && !isServerError409 && (
            <div className="mbe-4">
              <Banner variant="error">
                <FormattedMessage id="ERR_GENERIC" />
              </Banner>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-4">
              <TextField
                id="new-worker-name"
                label={intl.formatMessage({ id: "LABEL_WORKER_NAME" })}
                value={name}
                onChange={setName}
                autoComplete="off"
                data-testid="new-worker-name-input"
              />
              <TextField
                id="new-worker-pin"
                label={intl.formatMessage({ id: "LABEL_PIN" })}
                value={pin}
                onChange={setPin}
                autoComplete="off"
                data-testid="new-worker-pin-input"
              />
            </div>

            <div className="flex justify-end gap-3 mbs-8">
              <Button
                variant="secondary"
                type="button"
                onClick={handleClose}
                data-testid="new-worker-cancel-button"
              >
                <FormattedMessage id="LABEL_CANCEL" />
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={createWorker.isPending}
                data-testid="new-worker-submit-button"
              >
                <FormattedMessage id="LABEL_CREATE_WORKER" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </dialog>
  );
}
